import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Standard = () => {
  const { t } = useTranslation();

  const tabs = [
    { value: 'origin', label: t('standard.tabs.origin') },
    { value: 'head', label: t('standard.tabs.head') },
    { value: 'body', label: t('standard.tabs.body') },
    { value: 'coat', label: t('standard.tabs.coat') },
    { value: 'size', label: t('standard.tabs.size') },
    { value: 'faults', label: t('standard.tabs.faults') },
    { value: 'health', label: t('standard.tabs.health') },
    { value: 'nutrition', label: t('standard.tabs.nutrition') },
    { value: 'exercise', label: t('standard.tabs.exercise') }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-12 text-gray-900">
            {t('standard.title')}
          </h1>

          <Tabs defaultValue="origin" className="w-full">
            <TabsList className="w-full flex flex-wrap justify-start mb-8 bg-white p-2 rounded-lg shadow-md">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="px-4 py-2 text-sm font-medium data-[state=active]:bg-red-600 data-[state=active]:text-white"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="origin" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Köken, Sınıflandırma ve Tarihçe</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cane Corso, İtalya kökenli antik bir koruma ve çalışma köpeği ırkıdır. 
                  Roma döneminden beri var olan bu ırk, tarım alanlarında, mülklerin korunmasında 
                  ve av köpeği olarak kullanılmıştır.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>FCI Sınıflandırma:</strong> Grup 2 (Pinscher ve Schnauzer, Molosser ve İsviçre Dağ ve Çoban Köpekleri), 
                  Bölüm 2.1 (Molosser tipi köpekler)
                </p>
                <p className="text-gray-700 leading-relaxed">
                  1990'lı yıllarda yeniden canlandırılan ırk, 2007 yılında FCI tarafından resmi olarak tanınmıştır.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="head" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Kafa ve Yüz Yapısı</h2>
              <div className="prose max-w-none">
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Kafa:</strong> Geniş, molossoid tip. Alın geniş ve hafif kavislidir.</li>
                  <li><strong>Burun:</strong> Büyük, siyah renkli, geniş burun delikleri.</li>
                  <li><strong>Çene:</strong> Çok güçlü, kare yapılı. Hafif alt çene öne çıkması (undershot).</li>
                  <li><strong>Gözler:</strong> Orta boy, oval şekilli, koyu renk tercih edilir.</li>
                  <li><strong>Kulaklar:</strong> Üçgen şekilli, orta boy, yüksek yerleşimli.</li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="body" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Vücut, Kuyruk ve Hareket</h2>
              <div className="prose max-w-none">
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Vücut:</strong> Uzunluğu omuz yüksekliğinden biraz daha fazla. Güçlü ve kaslı yapı.</li>
                  <li><strong>Göğüs:</strong> İyi gelişmiş, geniş ve derin.</li>
                  <li><strong>Sırt:</strong> Düz, çok kaslı ve sağlam.</li>
                  <li><strong>Kuyruk:</strong> Kalın tabanlı, 4. omurda kesilir (kesme yapılmıyorsa doğal uzunlukta).</li>
                  <li><strong>Hareket:</strong> Uzun adımlar, güçlü ve etkili. Elastik tırıs.</li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="coat" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Kürk ve Renkler</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Kürk:</strong> Kısa, parlak, sıkı dokulu, az alt tüylü. Çok ince değil.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4"><strong>Kabul Edilen Renkler:</strong></p>
                <ul className="space-y-2 text-gray-700">
                  <li>Siyah</li>
                  <li>Kurşuni (açık gri, kurşun grisi, koyu gri)</li>
                  <li>Açık gri</li>
                  <li>Açık kızıl geyik</li>
                  <li>Kızıl geyik</li>
                  <li>Koyu kızıl geyik</li>
                  <li>Buğday rengi (brindle) - farklı tonlarda</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Kızıl geyik ve brindle köpeklerde yüzde siyah veya gri maske bulunabilir. 
                  Küçük beyaz lekeler göğüste, ayak parmaklarında ve burun köprüsünde kabul edilir.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="size" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Boyutlar ve Ağırlık</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-red-50 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Erkekler</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Yükseklik:</strong> 64-68 cm (omuz hizası)</li>
                    <li><strong>Ağırlık:</strong> 45-50 kg</li>
                  </ul>
                </div>
                <div className="p-6 bg-red-50 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-gray-900">Dişiler</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li><strong>Yükseklik:</strong> 60-64 cm (omuz hizası)</li>
                    <li><strong>Ağırlık:</strong> 40-45 kg</li>
                  </ul>
                </div>
              </div>
              <p className="text-gray-600 mt-6 text-sm">
                * 2 cm tolerans kabul edilir (erkekler için 62-70 cm, dişiler için 58-66 cm)
              </p>
            </TabsContent>

            <TabsContent value="faults" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Hatalar ve Diskalifiye Nedenleri</h2>
              <div className="prose max-w-none">
                <h3 className="text-lg font-semibold mb-3 text-red-600">Ciddi Hatalar:</h3>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li>Paralel veya birbirine yakın baş-burun eksenleri</li>
                  <li>Kısmen depigmente burun</li>
                  <li>Makas ısırık</li>
                  <li>Kuyruk kıvrık veya dikey taşınması</li>
                  <li>Sürekli tırıs gait</li>
                </ul>
                <h3 className="text-lg font-semibold mb-3 text-red-600">Diskalifiye Nedenleri:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>Aşırı çekingen veya agresif davranış</li>
                  <li>Burun köprüsü düşük (konkav)</li>
                  <li>Tamamen depigmente burun</li>
                  <li>Burun köprüsü ters kıvrımlı (ram nose)</li>
                  <li>Göz rengi mavi veya çift renkli</li>
                  <li>Kuyruksuz</li>
                  <li>Standart dışı renkler</li>
                  <li>Büyük beyaz lekeler</li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="health" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Sağlık</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cane Corso genel olarak sağlıklı bir ırktır, ancak büyük ırkların yaygın sağlık sorunlarına dikkat edilmelidir.
                </p>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Yaygın Sağlık Sorunları:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Kalça Displazisi (HD):</strong> Kalça ekleminin gelişim bozukluğu. Üretimde HD testleri zorunludur.</li>
                  <li><strong>Dirsek Displazisi (ED):</strong> Dirsek ekleminin gelişim bozukluğu.</li>
                  <li><strong>Göz Problemleri:</strong> Entropion (göz kapağı içe dönmesi), ektropion (dışa dönme).</li>
                  <li><strong>Mide Burulması:</strong> Derin göğüslü ırklarda yaygın, acil müdahale gerektirir.</li>
                  <li><strong>Kalp Hastalıkları:</strong> Dilate kardiyomiyopati gibi kalp problemleri.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-6">
                  Düzenli veteriner kontrolleri, dengeli beslenme ve uygun egzersiz ile sağlıklı bir yaşam sürdürülebilir.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="nutrition" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Beslenme</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cane Corso'lar büyük ve aktif köpeklerdir, bu nedenle yüksek kaliteli protein ve dengeli beslenme önemlidir.
                </p>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Beslenme İlkeleri:</h3>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li><strong>Yüksek Protein:</strong> Kas gelişimi için et bazlı yüksek protein içerikli mama.</li>
                  <li><strong>Kontrollü Kalsiyum:</strong> Yavru döneminde aşırı kalsiyum kemik problemlerine yol açabilir.</li>
                  <li><strong>Porsiyon Kontrolü:</strong> Obeziteden kaçınmak için ölçülü beslenme.</li>
                  <li><strong>Günde 2 Öğün:</strong> Yetişkin köpekler için günde 2 öğün önerilir.</li>
                </ul>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Yaş Gruplarına Göre Beslenme:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Yavru (2-12 ay):</strong> Büyük ırk yavru maması, günde 3-4 öğün</li>
                  <li><strong>Genç (12-24 ay):</strong> Genç/yetişkin maması, günde 2-3 öğün</li>
                  <li><strong>Yetişkin (2+ yaş):</strong> Yetişkin köpek maması, günde 2 öğün</li>
                  <li><strong>Yaşlı (7+ yaş):</strong> Senior köpek maması, günde 2 öğün</li>
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="exercise" className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Egzersiz</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cane Corso aktif ve enerjik bir ırktır. Fiziksel ve zihinsel sağlığı için düzenli egzersiz şarttır.
                </p>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Egzersiz Gereksinimleri:</h3>
                <ul className="space-y-3 text-gray-700 mb-6">
                  <li><strong>Günlük Yürüyüş:</strong> En az 1-2 saat yürüyüş veya koşu</li>
                  <li><strong>Oyun Zamanı:</strong> Top oyunu, frisbee gibi interaktif oyunlar</li>
                  <li><strong>Zihinsel Aktivite:</strong> Eğitim, komut çalışmaları, koklamak tabanlı oyunlar</li>
                  <li><strong>Sosyalleşme:</strong> Diğer köpeklerle kontrollü etkileşim</li>
                </ul>
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
                  <h4 className="font-semibold text-gray-900 mb-2">⚠️ Yavru Döneminde Dikkat:</h4>
                  <p className="text-gray-700 text-sm">
                    12 ay altındaki yavruların kemik gelişimi devam ettiği için aşırı egzersizden kaçının. 
                    Sert zeminlerde koşturma, merdiven çıkma/inme ve yüksek atlama gibi aktiviteleri sınırlandırın.
                  </p>
                </div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Önerilen Aktiviteler:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>Sabah ve akşam yürüyüşleri (her biri 30-60 dakika)</li>
                  <li>Güvenli, kapalı alanda serbest koşu</li>
                  <li>İzleme ve koku çalışmaları</li>
                  <li>Itaat eğitimi seansları</li>
                  <li>Yüzme (eklemler için harika egzersiz)</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Standard;
